import type * as Monaco from 'monaco-editor';
import type { SqlEditorSchema } from './types';

const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING',
  'LIMIT', 'OFFSET', 'AS', 'AND', 'OR', 'NOT', 'IN', 'BETWEEN',
  'LIKE', 'IS', 'NULL', 'TRUE', 'FALSE', 'CASE', 'WHEN', 'THEN',
  'ELSE', 'END', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'ON',
  'DISTINCT', 'ASC', 'DESC', 'CAST', 'COALESCE', 'NULLIF',
  'WITH', 'UNION', 'ALL', 'EXISTS',
];

const SQL_FUNCTIONS = [
  'SUM', 'AVG', 'COUNT', 'MIN', 'MAX', 'STDDEV', 'VARIANCE', 'MEDIAN',
  'ROUND', 'FLOOR', 'CEIL', 'ABS', 'POWER', 'SQRT', 'LOG', 'LN',
  'UPPER', 'LOWER', 'TRIM', 'LENGTH', 'SUBSTRING', 'REPLACE', 'CONCAT',
  'DATE_TRUNC', 'DATE_PART', 'EXTRACT', 'STRFTIME', 'CURRENT_DATE',
  'CURRENT_TIMESTAMP', 'NOW', 'INTERVAL',
  'IF', 'IIF', 'IFNULL',
];

const NUMERIC_TYPES = new Set([
  'integer', 'double', 'float', 'numeric', 'decimal', 'bigint', 'smallint',
  'int', 'int8', 'int16', 'int32', 'int64',
  'float16', 'float32', 'float64',
]);

/**
 * Register a CompletionItemProvider for SQL that provides:
 * - SQL keywords and functions
 * - View field columns from schema
 * - Template variables like {{WHERE_CLAUSE}}
 */
export function registerSqlCompletionProvider(
  monaco: typeof Monaco,
  schema: SqlEditorSchema,
): Monaco.IDisposable {
  return monaco.languages.registerCompletionItemProvider('sql', {
    triggerCharacters: ['.', '{', ' '],

    provideCompletionItems(model, position) {
      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });

      const word = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      };

      const suggestions: Monaco.languages.CompletionItem[] = [];

      // Check if inside an aggregation function → suggest only numeric fields
      const insideAgg = /\b(SUM|AVG|STDDEV|VARIANCE)\s*\(\s*[^)]*$/i.test(textUntilPosition);

      // Check if right after FROM → suggest view name
      const afterFrom = /\bFROM\s+$/i.test(textUntilPosition);

      // Check if after FROM <viewName> → suggest {{WHERE_CLAUSE}}
      const afterViewName = schema.viewName
        && new RegExp(`\\bFROM\\s+"?${schema.viewName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"?\\s+$`, 'i').test(textUntilPosition);

      // Check if after {{ → suggest variables
      if (textUntilPosition.endsWith('{{') || textUntilPosition.endsWith('{')) {
        const doubleOpen = textUntilPosition.endsWith('{{');

        // Build a range that includes the opening { or {{ AND any auto-closed }
        // after the cursor, so the full replacement is clean.
        const openLen = doubleOpen ? 2 : 1;
        const lineContent = model.getLineContent(position.lineNumber);
        const afterCursor = lineContent.substring(position.column - 1);
        const trailingBraces = afterCursor.match(/^\}+/);
        const closeLen = trailingBraces ? trailingBraces[0].length : 0;

        const varRange: Monaco.IRange = {
          startLineNumber: position.lineNumber,
          startColumn: position.column - openLen,
          endLineNumber: position.lineNumber,
          endColumn: position.column + closeLen,
        };

        for (const v of schema.variables) {
          // Normalize: strip any surrounding {{ }} to get the bare name
          const bare = v.replace(/^\{\{/, '').replace(/\}\}$/, '');
          suggestions.push({
            label: v,
            kind: monaco.languages.CompletionItemKind.Variable,
            insertText: `{{${bare}}}`,
            range: varRange,
            detail: 'Template variable',
            sortText: '0' + v,
          });
        }
        return { suggestions };
      }

      // View name suggestion (after FROM)
      if (afterFrom && schema.viewName) {
        suggestions.push({
          label: schema.viewName,
          kind: monaco.languages.CompletionItemKind.Struct,
          insertText: `"${schema.viewName}"`,
          range,
          detail: 'Dataset view',
          sortText: '0' + schema.viewName,
        });
      }

      // {{WHERE_CLAUSE}} suggestion (after FROM <viewName>)
      if (afterViewName) {
        suggestions.push({
          label: '{{WHERE_CLAUSE}}',
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: '{{WHERE_CLAUSE}}',
          range,
          detail: 'Dashboard filter placeholder',
          documentation: 'Injects runtime dashboard filters. Always placed after FROM <view>.',
          sortText: '0{{WHERE_CLAUSE}}',
        });
      }

      // SQL keywords
      for (const kw of SQL_KEYWORDS) {
        suggestions.push({
          label: kw,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: kw,
          range,
          sortText: '2' + kw,
        });
      }

      // SQL functions
      for (const fn of SQL_FUNCTIONS) {
        suggestions.push({
          label: fn,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: fn + '($0)',
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: 'SQL function',
          sortText: '1' + fn,
        });
      }

      // View fields
      for (const field of schema.fields) {
        // If inside aggregation, only suggest numeric fields (by type or semantic_type)
        if (insideAgg && !NUMERIC_TYPES.has(field.type) && field.semantic_type !== 'measure') continue;

        const semanticIcon = field.semantic_type === 'measure'
          ? monaco.languages.CompletionItemKind.Value
          : field.semantic_type === 'time'
            ? monaco.languages.CompletionItemKind.Event
            : monaco.languages.CompletionItemKind.Field;

        suggestions.push({
          label: field.name,
          kind: semanticIcon,
          insertText: `"${field.name}"`,
          range,
          detail: `${field.type} · ${field.semantic_type}`,
          documentation: field.display_name || field.name,
          sortText: '0' + field.name,
        });
      }

      // Variables
      for (const v of schema.variables) {
        suggestions.push({
          label: v,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: v,
          range,
          detail: 'Template variable',
          sortText: '0' + v,
        });
      }

      return { suggestions };
    },
  });
}